import React, { useContext, useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    Button,
    Table,
    UncontrolledTooltip,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Label,
} from 'reactstrap';
import SearchBar from '../../../components/SearchBar.js';
import AddRxForm from './components/AddRxForm.js';
import useSearchRx from '../../../services/commonServices/useSearchRx.js';
import { SelectedFacilitiesContext } from '../../../contexts/SelectedFacilitiesContext.js';
import { UserContext } from '../../../contexts/UserContext.js';
import { ButtonLoader } from '../../../components/Loader.js';
import DropdownComponent from '../../../components/Dropdown.js';
import useSaveOrderReturn from '../../../services/orderReturns/useSaveOrderReturn.js';
import useUpdateOrderReturn from '../../../services/orderReturns/useUpdateOrderReturn.js';
import { FaSearch, FaEdit } from 'react-icons/fa';
import { MdOutlineDeleteForever } from 'react-icons/md';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { hasRolePermission } from '../../../utility-services/rolePermissions.js';
import useExportFile from '../../../services/commonServices/useExportFile.js';
import usePrintPDF from '../../../services/commonServices/usePrintPDF.js';
import CreatePDFModal from './components/CreatePDFModal.js';

function OrderReturn() {
    const history = useHistory();
    const location = useLocation();
    const { facilities } = useContext(SelectedFacilitiesContext);
    const { user, roles } = useContext(UserContext);
    const canCreateOrderReturn = hasRolePermission(roles, 'canCreateOrderReturns');
    const canDeleteOrderReturn = hasRolePermission(roles, 'canDeleteOrderReturns');
    const { exportFile, isExportFileLoading } = useExportFile();
    const [activeExport, setActiveExport] = useState(null);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const { printPDF, isPrintPDFLoading, printPDFResponse } = usePrintPDF();
    const [isCreatePDFModalOpen, setIsCreatePDFModalOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchAttempt, setSearchAttempt] = useState(0);
    const [searchClearTrigger, setSearchClearTrigger] = useState(0);
    const [selectedDropdownValue, setSelectedDropdownValue] = useState('');
    const [dispensedt, setDispensedt] = useState('');
    const [openDispenseDateModal, setOpenDispenseDateModal] = useState(false);
    const [manualEntryMode, setManualEntryMode] = useState(false);
    const [destructionOnlyConfirmed, setDestructionOnlyConfirmed] = useState(false);
    const [pendingReturns, setPendingReturns] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editFieldsUnlocked, setEditFieldsUnlocked] = useState(false);
    const [editOrderReturnId, setEditOrderReturnId] = useState(null);
    const { saveOrderReturn, isSaveOrderReturnLoading } = useSaveOrderReturn();
    const { updateOrderReturn, isUpdateOrderReturnLoading } = useUpdateOrderReturn();

    const [rxLookupFacId, setRxLookupFacId] = useState('');

    const handleNotReturnableDecision = (confirmed) => {
        setDestructionOnlyConfirmed(confirmed);
    };

    const { searchRxData, isSearchRxLoading } = useSearchRx(
        searchInputValue,
        rxLookupFacId,
        user,
        dispensedt,
        setOpenDispenseDateModal,
        '/orderReturns/rxLookup',
        searchAttempt,
        handleNotReturnableDecision
    );

    const handleRxAdded = () => {
        setSearchInputValue(null);
        setSearchValue('');
        setSearchClearTrigger((prev) => prev + 1);
        setDispensedt('');
        setOpenDispenseDateModal(false);
        setRxLookupFacId('');
        setDestructionOnlyConfirmed(false);
    };

    useEffect(() => {
        const editItems = location.state?.editItems;
        if (!Array.isArray(editItems) || editItems.length === 0) return;
        const editFacId = location.state?.facId ?? editItems[0]?.facId ?? '';
        const mapped = editItems.map((item, index) => ({
            id: `edit-${Date.now()}-${index}`,
            itemId: item?.itemId ?? 0,
            rxNumber: item?.rxNumber ?? '',
            facId: item?.facId ?? editFacId,
            residentName: item?.residentName ?? '',
            residentId: item?.residentId ?? '',
            drugName: item?.drugName ?? '',
            dispensedQuantity: item?.dispensedQuantity ?? '',
            dispenseDate: item?.dispenseDate ?? '',
            quantity: item?.quantity ?? '',
            returnDate: item?.returnDate ?? new Date().toISOString(),
            allowReturn: item?.allowReturn ?? true,
            blockReturnReason: item?.blockReturnReason ?? '',
            controlledDrug: item?.controlledDrug ?? false,
            isManual: item?.isManual ?? item?.IsManual ?? false,
            destructionOnly: item?.destructionOnly ?? item?.IsDestructionOnly ?? false,
        }));
        setPendingReturns(mapped);
        setIsEditMode(true);
        setEditFieldsUnlocked(true);
        setEditOrderReturnId(location.state?.orderReturnId ?? null);
        if (editFacId) {
            setSelectedDropdownValue(editFacId);
        }
        history.replace({ pathname: location.pathname, state: {} });
    }, [location.state, location.pathname, history]);

    const handleManualEntryClick = () => {
        if (!selectedDropdownValue) {
            toast.error('You must select a facility from facility dropdown');
            return;
        }
        setManualEntryMode((prev) => !prev);
    };

    const handleReturnAdded = (row) => {
        setPendingReturns((prev) => [...prev, row]);
    };

    const handleRemovePending = (id) => {
        setPendingReturns((prev) => prev.filter((row) => row.id !== id));
    };

    const resetReturnPage = () => {
        setPendingReturns([]);
        setSearchInputValue(null);
        setSearchValue('');
        setSearchClearTrigger((prev) => prev + 1);
        setDispensedt('');
        setOpenDispenseDateModal(false);
        setRxLookupFacId('');
        setManualEntryMode(false);
        setDestructionOnlyConfirmed(false);
        setIsEditMode(false);
        setEditFieldsUnlocked(false);
        setEditOrderReturnId(null);
    };

    const hasMultiplePendingReturnFacilities = () => {
        const uniqueFacilityIds = new Set(
            pendingReturns
                .map((item) => item?.facId)
                .filter((facId) => facId !== undefined && facId !== null && facId !== '')
        );

        return uniqueFacilityIds.size > 1;
    };

    const buildOrderReturnPayload = (row) => ({
        Id: row.itemId ?? 0,
        RxNumber: row.rxNumber,
        FacId: row.facId,
        PatientName: row.residentName,
        PatientId: row.residentId,
        DrugLabelName: row.drugName,
        DispenseDate: row.dispenseDate,
        Amount: row.quantity,
        Request: 'Return',
        AllowReturn: row.allowReturn,
        BlockReturnReason: row.blockReturnReason,
        ControlledDrug: row.controlledDrug,
        IsDestructionOnly: row.destructionOnly ?? false,
        // RemainingQty: row.remainingQuantity,
        UserId: user?.userId,
        // PakingSlipID: '',
        IsManual: row.isManual,
    });

    const handleSaveSuccess = (data) => {
        const savedParentId = data?.orderReturnId ?? data?.OrderReturnId ?? data?.data?.orderReturnId ?? null;
        const savedItems = data?.items ?? data?.data?.items ?? null;

        setSearchInputValue(null);
        setSearchValue('');
        setSearchClearTrigger((prev) => prev + 1);
        setDispensedt('');
        setOpenDispenseDateModal(false);
        setRxLookupFacId('');
        setManualEntryMode(false);

        if (!savedParentId) {
            resetReturnPage();
            return;
        }

        const nextPendingReturns =
            Array.isArray(savedItems) && savedItems.length === pendingReturns.length
                ? pendingReturns.map((row, index) => ({ ...row, itemId: savedItems[index]?.id ?? row.itemId }))
                : pendingReturns;

        setPendingReturns(nextPendingReturns);
        setEditOrderReturnId(savedParentId);
        setIsEditMode(true);
        setEditFieldsUnlocked(false);
    };

    const handleSaveReturn = () => {
        if (pendingReturns.length === 0) return;

        if (facilityOptions.length > 1 && hasMultiplePendingReturnFacilities()) {
            showSingleFacilityValidation();
            return;
        }

        const payload = pendingReturns.map(buildOrderReturnPayload);
        saveOrderReturn(payload, {
            onSuccess: handleSaveSuccess,
        });
    };

    const handleUpdateReturn = () => {
        if (pendingReturns.length === 0) return;

        if (facilityOptions.length > 1 && hasMultiplePendingReturnFacilities()) {
            showSingleFacilityValidation();
            return;
        }

        const items = pendingReturns.map(buildOrderReturnPayload);
        updateOrderReturn({ orderReturnId: editOrderReturnId, items }, { onSuccess: resetReturnPage });
    };

    const handleSearch = (value) => {
        if (typeof value !== 'string') {
            return;
        }
        if (!value.trim()) {
            toast.error('Please enter an Rx # before searching.');
            return;
        }
        setManualEntryMode(false);
        setDestructionOnlyConfirmed(false);
        setRxLookupFacId(selectedDropdownValue || '');
        setSearchInputValue(value);
        setSearchAttempt((prev) => prev + 1);
    };

    const getSavedItemIds = () =>
        pendingReturns.map((row) => row.itemId).filter((id) => typeof id === 'number' && id > 0);

    const toggleExportDropdown = () => setExportDropdownOpen((prev) => !prev);

    const handleExportExcel = () => {
        const ids = getSavedItemIds();
        if (ids.length === 0) return;
        const params = new URLSearchParams();
        ids.forEach((id) => params.append('ids', id));
        params.append('exportType', 'Excel');
        setActiveExport('excel');
        exportFile(
            { url: '/orderReturns/export', fileName: 'order-returns.xlsx', params },
            { onSettled: () => setActiveExport(null) }
        );
    };

    const handlePrintItems = () => {
        const ids = getSavedItemIds();
        if (ids.length === 0) return;

        printPDF(
            {
                url: '/orderReturns/packingSlip',
                fileName: 'packing-slip.pdf',
                downloadOnSuccess: false,
                payload: {
                    UserId: user?.userId,
                    OrderReturnItemIds: ids,
                },
            },
            {
                onSuccess: () => {
                    setIsCreatePDFModalOpen(true);
                },
            }
        );
    };

    // select facilities from dropdown
    const facilityOptions = facilities
        .filter((fac) => fac?.facName != null && fac?.facID != null)
        .map((fac) => ({
            label: `(${fac.facID})  ${fac.facName} `,
            value: fac.facID,
        }));

    const handleDropdownChange = (selectedValue) => {
        setSelectedDropdownValue(selectedValue);
    };

    useEffect(() => {
        if (facilityOptions.length === 1) {
            setSelectedDropdownValue(facilityOptions[0].value);
        }
    }, [facilities]);

    const showSingleFacilityValidation = () => {
        Swal.fire({
            icon: 'info',
            text: 'Please select records from 1 facility before proceeding.',
            customClass: {
                confirmButton: 'custom-green-button',
            },
        });
    };

    const allowReturn = searchRxData[0]?.allowReturn;

    const commonFieldsLocked = isEditMode ? !editFieldsUnlocked : pendingReturns.length > 0;

    const addControlsLocked = isEditMode ? !editFieldsUnlocked : !selectedDropdownValue;
    return (
        <div className="page-content">
            <div className="d-flex align-items-center justify-content-between">
                <h2 className="mb-2">Order Returns</h2>
                <div className="d-flex" style={{ gap: '8px' }}>
                    <Button
                        tag={Link}
                        to="/utilities/order-return/saved"
                        style={{
                            cursor: 'pointer',
                            borderRadius: '5px',
                            padding: '8px 15px',
                            border: 'none',
                            outline: 'none',
                            color: 'white',
                            backgroundColor: '#6952AD',
                        }}>
                        Saved Returns
                    </Button>
                    <Button
                        tag={Link}
                        to="/utilities/order-return/history"
                        style={{
                            cursor: 'pointer',
                            borderRadius: '5px',
                            padding: '8px 15px',
                            border: 'none',
                            outline: 'none',
                            color: 'white',
                            backgroundColor: '#727cf5',
                        }}>
                        Order return history
                    </Button>
                </div>
            </div>
            <Row className="bg-white" style={{ borderRadius: '7px', width: '100%' }}>
                <Col className="p-3">
                    <div className="mb-0">
                        <Label className="mb-2 d-block">Facility Name:</Label>
                        {facilityOptions.length > 1 ? (
                            <div style={{ width: '40%' }}>
                                <span id="order-return-facility-wrapper" style={{ display: 'block' }}>
                                    <DropdownComponent
                                        options={facilityOptions}
                                        label=""
                                        placeholder="Choose a facility"
                                        selectedDropdownValue={selectedDropdownValue}
                                        setSelectedDropdownValue={handleDropdownChange}
                                        showEmptyOption={false}
                                        emptyOptionLabel="Choose a facility"
                                        disabled={commonFieldsLocked}
                                    />
                                </span>
                                {commonFieldsLocked && (
                                    <UncontrolledTooltip placement="top" target="order-return-facility-wrapper">
                                        The facility cannot be changed once items have been added.
                                    </UncontrolledTooltip>
                                )}
                            </div>
                        ) : facilityOptions.length === 1 ? (
                            <div
                                className="border"
                                style={{
                                    width: '40%',
                                    padding: '8px 12px',
                                    fontWeight: '500',
                                    color: '#333',
                                    fontSize: '14px',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                {facilityOptions[0]?.label}
                            </div>
                        ) : null}
                    </div>
                    {selectedDropdownValue && (
                    <div
                        className="mt-3"
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div
                                style={{ gap: '8px', flex: '1 1 auto', minWidth: 0 }}
                                className="d-flex align-items-center justify-content-start gap-3">
                                <div style={{ width: '45%' }}>
                                    <SearchBar
                                        searchPlaceholder="Search Rx #"
                                        onSearch={handleSearch}
                                        onValueChange={setSearchValue}
                                        clearTrigger={searchClearTrigger}
                                    />
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2" style={{ gap: '8px', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    className="btn btn-primary d-flex align-items-center gap-2 w-fit"
                                    onClick={() => handleSearch(searchValue)}
                                    disabled={addControlsLocked}
                                    style={{ gap: '5px', whiteSpace: 'nowrap' }}>
                                    <FaSearch />
                                    Search
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2 w-fit"
                                    onClick={handleManualEntryClick}
                                    disabled={addControlsLocked}
                                    style={{ gap: '5px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    <FaEdit />
                                    {manualEntryMode ? 'Cancel Manual Entry' : 'Manual Entry'}
                                </button>
                            </div>
                        </div>

                        {isSearchRxLoading && (
                            <Card
                                style={{
                                    fontStyle: 'italic',
                                    color: '#6B7280',
                                    height: '100px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'white',
                                    borderRadius: '5px',
                                }}>
                                <ButtonLoader />
                            </Card>
                        )}
                        {manualEntryMode ? (
                            <AddRxForm
                                manualMode
                                facId={selectedDropdownValue}
                                onReturnAdded={handleReturnAdded}
                                canCreate={canCreateOrderReturn}
                            />
                        ) : (
                            (allowReturn || destructionOnlyConfirmed) && (
                                <AddRxForm
                                    data={searchRxData}
                                    dispensedt={dispensedt}
                                    setDispensedt={setDispensedt}
                                    openDispenseDateModal={openDispenseDateModal}
                                    setOpenDispenseDateModal={setOpenDispenseDateModal}
                                    onReturnAdded={handleReturnAdded}
                                    onRxAdded={handleRxAdded}
                                    canCreate={canCreateOrderReturn}
                                    isDestructionOnly={!allowReturn && destructionOnlyConfirmed}
                                />
                            )
                        )}

                        {pendingReturns.length > 0 && (
                        <Card className="p-2">
                            <Row>
                                <Col md={12}>
                                    <div
                                        style={{
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            overflowX: 'auto',
                                            width: '100%',
                                        }}>
                                        <Table
                                            className="table-bordered"
                                            style={{
                                                marginBottom: '0',
                                                minWidth: '1000px',
                                                whiteSpace: 'nowrap',
                                            }}>
                                            <thead
                                                className="thead-light"
                                                style={{ position: 'sticky', top: '0', zIndex: '10' }}>
                                                <tr>
                                                    <th>Rx #</th>
                                                    <th>Facility</th>
                                                    <th>Resident ID</th>
                                                    <th>Resident</th>
                                                    <th>Drug Name</th>
                                                    <th>Dispense Date</th>
                                                    <th>Return Qty</th>
                                                    <th>Destruction Only</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pendingReturns.map((row) => (
                                                    <tr key={row.id}>
                                                        <td>{row.rxNumber || '-'}</td>
                                                        <td>{row.facId || '-'}</td>
                                                        <td>{row.residentId || '-'}</td>
                                                        <td>{row.residentName || '-'}</td>
                                                        <td>{row.drugName || '-'}</td>
                                                        <td>
                                                            {row.dispenseDate
                                                                ? new Date(row.dispenseDate).toLocaleDateString()
                                                                : '-'}
                                                        </td>
                                                        <td>{row.quantity ?? '-'}</td>
                                                        <td>
                                                            {row.destructionOnly ? (
                                                                <span
                                                                    role="checkbox"
                                                                    aria-checked="true"
                                                                    aria-label="Destruction only"
                                                                    style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: '16px',
                                                                        height: '16px',
                                                                        backgroundColor: '#2563EB',
                                                                        borderRadius: '3px',
                                                                        color: '#fff',
                                                                        fontSize: '11px',
                                                                        lineHeight: 1,
                                                                    }}>
                                                                    &#10003;
                                                                </span>
                                                            ) : (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={false}
                                                                    readOnly
                                                                    disabled
                                                                    aria-label="Destruction only"
                                                                />
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                aria-label="Remove line"
                                                                onClick={() => handleRemovePending(row.id)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    outline: 'none',
                                                                    border: 'none',
                                                                    backgroundColor: '#FFB0C4',
                                                                    borderRadius: '50%',
                                                                    padding: '5px',
                                                                    width: '30px',
                                                                    height: '30px',
                                                                }}>
                                                                <MdOutlineDeleteForever
                                                                    style={{ fontSize: '20px' }}
                                                                    color="red"
                                                                />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                        )}
                        {pendingReturns.length > 0 && (
                            <Row className="mt-3 mb-3">
                                <Col className="d-flex justify-content-end" style={{ gap: '10px' }}>
                                    {isEditMode && (
                                        <Dropdown isOpen={exportDropdownOpen} toggle={toggleExportDropdown}>
                                            <DropdownToggle caret color="secondary" outline>
                                                Export
                                            </DropdownToggle>
                                            <DropdownMenu right>
                                                <DropdownItem onClick={handlePrintItems} disabled={isPrintPDFLoading}>
                                                    {isPrintPDFLoading ? 'Printing...' : 'Create Packing Slip'}
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={handleExportExcel}
                                                    disabled={isExportFileLoading && activeExport === 'excel'}>
                                                    {isExportFileLoading && activeExport === 'excel'
                                                        ? 'Exporting...'
                                                        : 'Export Excel'}
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    )}
                                    <Button
                                        color="primary"
                                        onClick={isEditMode ? handleUpdateReturn : handleSaveReturn}
                                        disabled={
                                            isSaveOrderReturnLoading ||
                                            isUpdateOrderReturnLoading ||
                                            (isEditMode && !editFieldsUnlocked)
                                        }>
                                        {isSaveOrderReturnLoading || isUpdateOrderReturnLoading ? (
                                            <ButtonLoader />
                                        ) : isEditMode && editFieldsUnlocked ? (
                                            'Update Return'
                                        ) : (
                                            'Save Return'
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        )}
                    </div>
                    )}
                </Col>
            </Row>

            <CreatePDFModal
                isCreatePDFModalOpen={isCreatePDFModalOpen}
                setIsCreatePDFModalOpen={setIsCreatePDFModalOpen}
                selectedRows={pendingReturns.map((row) => ({
                    facId: row.facId,
                    rxNumber: row.rxNumber,
                    amount: row.quantity,
                }))}
                setSelectedRows={() => {}}
                printPDFResponse={printPDFResponse}
                refetchData={resetReturnPage}
            />
        </div>
    );
}

export default OrderReturn;
